import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography, Paper } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { logQuestionAsked } from './analytics';
import { Game } from '../utils/Common';

interface Props {
    questions: {
        standard?: Question[];
        unique?: Question[];
    },
    recordQuestion: (id: number) => Promise<number>;
    game: Game | null;
    gameCompleted?: boolean;
}

interface Question {
    question: string;
    answer: string;
    asked: boolean;
    id: string;
};

const NameQuestions: React.FC<Props> = (props) => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [uniqueProvided, setUniqueProvided] = useState(false);

    const onShowAnswer = async (question: Question) => {
        // Prevent execution if game is completed (check both sources)
        if (props.game?.gameCompleted || props.gameCompleted) {
            return;
        }
        var updatedQuestions = questions.map(q => {
            if (q.id === question.id) {
                logQuestionAsked(q.question);
                return {
                    ...q,
                    asked: true
                };
            }
            return q;
            
        });
        
        // Check if all currently visible questions have been asked
        if (updatedQuestions.every(q => q.asked) && !uniqueProvided) {
            // Add unique questions with asked state set to false initially (they need to be asked in current session)
            const uniqueQuestionsWithAskedState = (props.questions?.unique || []).map(q => ({
                ...q,
                asked: false // Always start unique questions as unasked when first dynamically added
            }));
            updatedQuestions = updatedQuestions.concat(uniqueQuestionsWithAskedState);
            setUniqueProvided(true);
        }
        
        setQuestions(updatedQuestions);

        // Record question through GameManager using passed-in function
        try {
            await props.recordQuestion(parseInt(question.id));
        } catch (error) {
            console.error('Error recording question through GameManager:', error);
        }
    };

    useEffect(() => {
        const standardQuestions = props.questions?.standard || [];
        const uniqueQuestions = props.questions?.unique || [];
        const askedIds = props.game?.asks || [];

        // Mark standard questions as asked if their ID is in the game's asks array
        const questionsWithAskedState = standardQuestions.map(q => ({
            ...q,
            asked: askedIds.includes(parseInt(q.id))
        }));

        let allQuestions = [...questionsWithAskedState];

        // Check if all standard questions have been asked
        const allStandardAsked = questionsWithAskedState.length > 0 && questionsWithAskedState.every(q => q.asked);

        if (allStandardAsked && !uniqueProvided) {
            // Add unique questions - mark them as asked if they were previously asked
            const uniqueWithAskedState = uniqueQuestions.map(q => {
                const wasAskedPreviously = askedIds.includes(parseInt(q.id));
                return {
                    ...q,
                    asked: wasAskedPreviously
                };
            });
            allQuestions = [...allQuestions, ...uniqueWithAskedState];
            setUniqueProvided(true);
        } else if (allStandardAsked && uniqueProvided) {
            // Unique questions are already provided, just update their asked state
            const uniqueWithAskedState = uniqueQuestions.map(q => {
                const wasAskedPreviously = askedIds.includes(parseInt(q.id));
                return {
                    ...q,
                    asked: wasAskedPreviously
                };
            });
            allQuestions = [...allQuestions, ...uniqueWithAskedState];
        }

        setQuestions(allQuestions);
    }, [props.questions, props.game?.asks]);

    return (
        <Box sx={{ mb: 2 }}>
            <Paper elevation={3} sx={{ mb: 2 }}>
                <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            minHeight: { xs: 36, sm: 40, md: 44, lg: 48 },
                            '&.Mui-expanded': {
                                minHeight: { xs: 36, sm: 40, md: 44, lg: 48 }
                            },
                            '& .MuiAccordionSummary-content': {
                                my: { xs: 0.25, sm: 0.5, md: 0.75, lg: 1 }
                            },
                            '& .MuiAccordionSummary-content.Mui-expanded': {
                                my: { xs: 0.25, sm: 0.375, md: 0.4375, lg: 0.5 }
                            },
                            '& .MuiAccordionSummary-expandIconWrapper': {
                                color: 'white'
                            }
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 'bold',
                                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem', lg: '1.25rem' },
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Questions
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 0.5, sm: 0.4, md: 0.94, lg: 2 }, py: { xs: 0.5, sm: 0.4, md: 0.94, lg: 2 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.75, sm: 1, md: 1.25, lg: 1.5 } }}>
                            {questions.map((question, index) => (
                                <Paper
                                    key={`question-${question.id}-${index}`}
                                    elevation={question.asked ? 0 : 1}
                                    sx={{
                                        px: { xs: 0.5, sm: 0.4, md: 0.75, lg: 2 },
                                        py: { xs: 0.5, sm: 0.4, md: 0.75, lg: 2 },
                                        backgroundColor: question.asked ? 'success.lighter' : 'background.paper',
                                        border: question.asked ? '1px solid' : 'none',
                                        borderColor: question.asked ? 'success.main' : 'transparent'
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: { xs: 0.5, sm: 1, md: 1.5, lg: 2 }
                                    }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem', lg: '1rem' },
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {question.question}
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            flexShrink: 0,
                                            minWidth: { xs: 80, sm: 100, md: 120, lg: 180 },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end'
                                        }}>
                                            {question.asked ? (
                                                <Typography
                                                    variant="body1"
                                                    color="success.main"
                                                    sx={{
                                                        fontWeight: 600,
                                                        textAlign: 'right',
                                                        width: '100%',
                                                        fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem', lg: '1rem' },
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {question.answer}
                                                </Typography>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => onShowAnswer(question)}
                                                    disabled={props.game?.gameCompleted || props.gameCompleted || false}
                                                    sx={{
                                                        width: '100%',
                                                        py: { xs: 0.5, sm: 0.4, md: 0.75, lg: 2 },
                                                        px: { xs: 0.5, sm: 0.4, md: 0.75, lg: 2 },
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.825rem', lg: '0.9rem' },
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    Show Answer
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Paper>
        </Box>
    );
};

export default NameQuestions;